// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0

/* eslint-disable class-methods-use-this */
import * as z from 'zod';
import { Decoder, Encoder } from 'cbor-x';
import { ACSKey, SerializedACSContainer } from '.';
import { ACSCache } from './ACSCache';

const DIRECTORY_NAME = 'acs' as const;

const sizeSchema = z.object({
  usageDetails: z
    .object({
      fileSystem: z.number().optional(),
    })
    .optional(),
});

export class ACSCacheOPFS extends ACSCache {
  private readonly encoder = new Encoder();

  public async set(serializedContainer: SerializedACSContainer): Promise<void> {
    const encodedContainer = this.encoder.encode(serializedContainer);

    const directoryHandle = await this.getDBDirectoryHandle();

    const contractsFileHandle = await directoryHandle.getFileHandle(serializedContainer.key, {
      create: true,
    });

    const writable = await contractsFileHandle.createWritable();

    await writable.write(encodedContainer);

    await writable.close();
  }

  public async get(key: ACSKey): Promise<SerializedACSContainer | null> {
    const directoryHandle = await this.getDBDirectoryHandle();

    try {
      const contractsFileHandle = await directoryHandle.getFileHandle(key, { create: false });

      const contractFile = await contractsFileHandle.getFile();

      const decoder = new Decoder();

      const buffer = new Uint8Array(await contractFile.arrayBuffer());
      const decodedContractsFile = (await decoder.decode(buffer)) as SerializedACSContainer;

      return decodedContractsFile;
    } catch (e) {
      // It means that this ACSKey is not cached yet, so it is not an actual error
      // but desired information.
      if (e instanceof DOMException && e.name === 'NotFoundError') {
        return null;
      }

      console.error(`Failed to get SerializedACSContainer!`, e);
      return null;
    }
  }

  public async getAllKeys(): Promise<ACSKey[]> {
    const directoryHandle = await this.getDBDirectoryHandle();

    const keys: ACSKey[] = [];

    if (!('keys' in directoryHandle && typeof directoryHandle.keys === 'function')) {
      console.error(`Failed to get keys from directoryHandle!`);
      return keys;
    }

    // eslint-disable-next-line no-restricted-syntax
    for await (const key of directoryHandle.keys()) {
      keys.push(key as ACSKey);
    }

    return keys;
  }

  public async has(key: ACSKey): Promise<boolean> {
    const directoryHandle = await this.getDBDirectoryHandle();

    try {
      const fileHandle = await directoryHandle.getFileHandle(key, { create: false });
      const file = await fileHandle.getFile();

      return !!file;
    } catch (e) {
      if (e instanceof DOMException && e.name === 'NotFoundError') {
        return false;
      }

      throw e;
    }
  }

  public async remove(key: ACSKey): Promise<void> {
    const directoryHandle = await this.getDBDirectoryHandle();

    await directoryHandle.removeEntry(key);
  }

  public async clear(): Promise<void> {
    const root = await navigator.storage.getDirectory();
    await root.removeEntry(DIRECTORY_NAME, { recursive: true });
  }

  public getSize(): Promise<number> {
    return navigator.storage.estimate().then((estimate) => {
      return sizeSchema.parse(estimate).usageDetails?.fileSystem ?? 0;
    });
  }

  private getDBDirectoryHandle(): Promise<FileSystemDirectoryHandle> {
    return navigator.storage.getDirectory().then((root) => {
      return root.getDirectoryHandle(DIRECTORY_NAME, {
        create: true,
      });
    });
  }
}
